import { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  Plus, Search, Filter, MoreVertical, Edit, Trash2, Copy, 
  Star, Flame, Sparkles, TrendingUp, Tag, Crown, Package,
  ChevronDown, Check, X, Upload, AlertCircle, Grid, List
} from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '../../components/ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Size options based on category
const SIZE_OPTIONS = {
  'clothing': ['S', 'M', 'L', 'XL', 'XXL'],
  'shirts': ['S', 'M', 'L', 'XL', 'XXL'],
  't-shirts': ['S', 'M', 'L', 'XL', 'XXL'],
  'polo': ['S', 'M', 'L', 'XL', 'XXL'],
  'big size shirts': ['3XL', '4XL', '5XL', '6XL'],
  'plus size': ['3XL', '4XL', '5XL', '6XL'],
  'jeans': ['28', '30', '32', '34', '36'],
  'big size jeans': ['38', '40', '42', '44', '46', '48'],
  'plus size jeans': ['38', '40', '42', '44', '46', '48'],
  'footwear': ['UK 3', 'UK 4', 'UK 5', 'UK 6', 'UK 7', 'UK 8', 'UK 9', 'UK 10', 'UK 11'],
  'shoes': ['UK 3', 'UK 4', 'UK 5', 'UK 6', 'UK 7', 'UK 8', 'UK 9', 'UK 10', 'UK 11'],
  'accessories': ['Free Size'],
  'perfume': ['Free Size'],
  'attar': ['Free Size'],
  'default': ['S', 'M', 'L', 'XL', 'XXL']
};

// Get sizes based on category name
const getSizesForCategory = (categoryName) => {
  if (!categoryName) return SIZE_OPTIONS.default;
  const lowerName = categoryName.toLowerCase();
  
  // Check for plus size / big size
  if (lowerName.includes('plus size') || lowerName.includes('big size')) {
    if (lowerName.includes('jeans')) return SIZE_OPTIONS['big size jeans'];
    return SIZE_OPTIONS['plus size'];
  }
  
  // Check for specific categories
  for (const [key, sizes] of Object.entries(SIZE_OPTIONS)) {
    if (lowerName.includes(key)) return sizes;
  }
  
  return SIZE_OPTIONS.default;
};

// Toggle Badge Component
const ToggleBadge = ({ active, label, icon: Icon, onClick, color = 'gold' }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium transition-all ${
      active 
        ? `bg-${color} text-white` 
        : 'bg-charcoal/5 text-charcoal-light hover:bg-charcoal/10'
    }`}
    title={label}
  >
    <Icon className="w-3 h-3" />
    <span className="hidden sm:inline">{label}</span>
  </button>
);

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filterType, setFilterType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal States
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: '',
    discount_price: '',
    category_id: '',
    subcategory_id: '',
    images: [],
    fabric: '',
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    stock: '',
    variants: [],
    tags: [],
    sku: '',
    is_featured: false,
    is_best_seller: false,
    is_new_arrival: false,
    is_super_selling: false,
    is_trending: false,
    is_offer: false,
    is_premium_collection: false
  });
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [searchQuery, selectedCategory, filterType, currentPage]);

  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const cats = response.data.categories || [];
      setCategories(cats);
      console.log('Categories loaded:', cats.length);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20
      });
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory) params.append('category', selectedCategory);
      if (filterType) params.append('filter_type', filterType);
      
      const response = await axios.get(`${API}/admin/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.products || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error loading products:', error);
      // Fallback to public endpoint
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data || []);
      } catch (e) {
        toast.error('Failed to load products');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const openAddModal = () => {
    setEditingProduct(null);
    // Use categories state directly - if empty, dropdown will show "Select Category"
    const firstCategoryId = categories.length > 0 ? categories[0].id : '';
    setFormData({
      name: '',
      slug: '',
      description: '',
      price: '',
      discount_price: '',
      category_id: firstCategoryId,
      subcategory_id: '',
      images: [],
      fabric: '',
      sizes: ['S', 'M', 'L', 'XL', '2XL'],
      stock: '',
      variants: [],
      tags: [],
      sku: '',
      is_featured: false,
      is_best_seller: false,
      is_new_arrival: false,
      is_super_selling: false,
      is_trending: false,
      is_offer: false,
      is_premium_collection: false
    });
    setActiveTab('basic');
    setShowProductModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      slug: product.slug || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      discount_price: product.discount_price?.toString() || '',
      category_id: product.category_id || '',
      subcategory_id: product.subcategory_id || '',
      images: product.images || [],
      fabric: product.fabric || '',
      sizes: product.sizes || ['S', 'M', 'L', 'XL', '2XL'],
      stock: product.stock?.toString() || product.total_stock?.toString() || '',
      variants: product.variants || [],
      tags: product.tags || [],
      sku: product.sku || '',
      is_featured: product.is_featured || false,
      is_best_seller: product.is_best_seller || false,
      is_new_arrival: product.is_new_arrival || false,
      is_super_selling: product.is_super_selling || false,
      is_trending: product.is_trending || false,
      is_offer: product.is_offer || false,
      is_premium_collection: product.is_premium_collection || false
    });
    setActiveTab('basic');
    setShowProductModal(true);
  };

  const handleSaveProduct = async () => {
    if (!formData.name || !formData.price || !formData.category_id) {
      toast.error('Please fill required fields: Name, Price, Category');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const productData = {
        ...formData,
        slug: formData.slug || generateSlug(formData.name),
        price: parseFloat(formData.price),
        discount_price: formData.discount_price ? parseFloat(formData.discount_price) : null,
        stock: formData.stock ? parseInt(formData.stock) : 0,
        total_stock: formData.stock ? parseInt(formData.stock) : 0
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/admin/products`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created successfully');
      }
      
      setShowProductModal(false);
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted');
      setShowDeleteConfirm(null);
      loadProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleDuplicateProduct = async (productId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/products/${productId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product duplicated');
      loadProducts();
    } catch (error) {
      toast.error('Failed to duplicate product');
    }
  };

  const handleToggleChange = async (productId, toggleName, currentValue) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/products/${productId}/toggles`, 
        { [toggleName]: !currentValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setProducts(products.map(p => 
        p.id === productId ? { ...p, [toggleName]: !currentValue } : p
      ));
      
      toast.success(`${toggleName.replace('is_', '').replace(/_/g, ' ')} ${!currentValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedProducts.length === 0) {
      toast.error('No products selected');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      
      if (action === 'delete') {
        if (!window.confirm(`Delete ${selectedProducts.length} products?`)) return;
        await axios.delete(`${API}/admin/products/bulk-delete`, {
          headers: { Authorization: `Bearer ${token}` },
          data: { product_ids: selectedProducts }
        });
        toast.success(`Deleted ${selectedProducts.length} products`);
      } else {
        await axios.post(`${API}/admin/products/bulk-update`, 
          { product_ids: selectedProducts, update_data: { [action]: true } },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Updated ${selectedProducts.length} products`);
      }
      
      setSelectedProducts([]);
      setShowBulkActions(false);
      loadProducts();
    } catch (error) {
      toast.error('Bulk action failed');
    }
  };

  const addVariant = () => {
    setFormData({
      ...formData,
      variants: [
        ...formData.variants,
        { id: `v-${Date.now()}`, size: 'M', color: 'Standard', stock: 100, sku: '', ean: '' }
      ]
    });
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...formData.variants];
    newVariants[index] = { ...newVariants[index], [field]: field === 'stock' ? parseInt(value) || 0 : value };
    setFormData({ ...formData, variants: newVariants });
  };

  const removeVariant = (index) => {
    setFormData({
      ...formData,
      variants: formData.variants.filter((_, i) => i !== index)
    });
  };

  const filterOptions = [
    { value: '', label: 'All Products' },
    { value: 'featured', label: 'Featured' },
    { value: 'best_seller', label: 'Best Sellers' },
    { value: 'new_arrival', label: 'New Arrivals' },
    { value: 'super_selling', label: 'Super Selling' },
    { value: 'trending', label: 'Trending' },
    { value: 'offer', label: 'Offers' },
    { value: 'premium_collection', label: 'Premium Collection' }
  ];

  return (
    <div className="space-y-6" data-testid="admin-products-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light text-charcoal">Products</h1>
          <p className="text-charcoal-light text-sm mt-1">
            Manage your product catalog ({products.length} products)
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="inline-flex items-center gap-2 bg-charcoal text-ivory px-6 py-3 text-xs tracking-wider uppercase font-medium hover:bg-charcoal/90 transition-colors"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4" />
          Add Product
        </button>
      </div>

      {/* Filters & Search */}
      <div className="bg-white border border-charcoal/10 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-light" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products by name, SKU, or tags..."
              className="w-full pl-10 pr-4 py-2.5 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold text-sm"
              data-testid="search-products"
            />
          </div>
          
          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2.5 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold text-sm min-w-[160px]"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          
          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2.5 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold text-sm min-w-[160px]"
          >
            {filterOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          
          {/* View Mode */}
          <div className="flex border border-charcoal/20">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 ${viewMode === 'grid' ? 'bg-charcoal text-ivory' : 'text-charcoal-light hover:bg-beige'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 ${viewMode === 'list' ? 'bg-charcoal text-ivory' : 'text-charcoal-light hover:bg-beige'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedProducts.length > 0 && (
          <div className="mt-4 p-3 bg-gold/10 border border-gold/20 flex items-center justify-between">
            <span className="text-sm text-charcoal">
              {selectedProducts.length} product(s) selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkAction('is_featured')}
                className="px-3 py-1.5 bg-gold text-white text-xs font-medium"
              >
                Mark Featured
              </button>
              <button
                onClick={() => handleBulkAction('is_best_seller')}
                className="px-3 py-1.5 bg-charcoal text-ivory text-xs font-medium"
              >
                Mark Best Seller
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedProducts([])}
                className="px-3 py-1.5 border border-charcoal/20 text-charcoal text-xs font-medium"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Products Grid/List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white border border-charcoal/10 p-12 text-center">
          <Package className="w-12 h-12 mx-auto mb-4 text-charcoal-light" />
          <h3 className="font-heading text-xl text-charcoal mb-2">No Products Found</h3>
          <p className="text-charcoal-light mb-6">Get started by adding your first product.</p>
          <button
            onClick={openAddModal}
            className="inline-flex items-center gap-2 bg-gold text-white px-6 py-3 text-xs tracking-wider uppercase font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.map(product => (
            <div 
              key={product.id} 
              className="bg-white border border-charcoal/10 group"
              data-testid={`product-card-${product.id}`}
            >
              {/* Checkbox */}
              <div className="absolute top-3 left-3 z-10">
                <input
                  type="checkbox"
                  checked={selectedProducts.includes(product.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedProducts([...selectedProducts, product.id]);
                    } else {
                      setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                    }
                  }}
                  className="w-4 h-4 accent-gold"
                />
              </div>
              
              {/* Image */}
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={product.images?.[0] || 'https://via.placeholder.com/300'}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {product.discount_price && (
                  <span className="absolute top-3 right-3 bg-red-600 text-white px-2 py-1 text-xs font-medium">
                    {Math.round(((product.price - product.discount_price) / product.price) * 100)}% OFF
                  </span>
                )}
                
                {/* Quick Actions */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => openEditModal(product)}
                      className="p-2 bg-white/90 text-charcoal hover:bg-white"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDuplicateProduct(product.id)}
                      className="p-2 bg-white/90 text-charcoal hover:bg-white"
                      title="Duplicate"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(product.id)}
                      className="p-2 bg-white/90 text-red-600 hover:bg-white"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Info */}
              <div className="p-4">
                <p className="text-xs text-charcoal-light mb-1">{product.category_name || 'Uncategorized'}</p>
                <h3 className="font-medium text-charcoal mb-2 line-clamp-2">{product.name}</h3>
                <div className="flex items-baseline gap-2 mb-3">
                  {product.discount_price ? (
                    <>
                      <span className="text-lg font-medium text-charcoal">₹{product.discount_price}</span>
                      <span className="text-sm text-charcoal-light line-through">₹{product.price}</span>
                    </>
                  ) : (
                    <span className="text-lg font-medium text-charcoal">₹{product.price}</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-charcoal-light">
                  <span>Stock: {product.total_stock || 0}</span>
                  <span>Sold: {product.sold_count || 0}</span>
                </div>
                
                {/* Toggle Badges */}
                <div className="flex flex-wrap gap-1 mt-3">
                  <ToggleBadge 
                    active={product.is_featured}
                    label="Featured"
                    icon={Star}
                    onClick={() => handleToggleChange(product.id, 'is_featured', product.is_featured)}
                  />
                  <ToggleBadge 
                    active={product.is_best_seller}
                    label="Best"
                    icon={Flame}
                    onClick={() => handleToggleChange(product.id, 'is_best_seller', product.is_best_seller)}
                  />
                  <ToggleBadge 
                    active={product.is_new_arrival}
                    label="New"
                    icon={Sparkles}
                    onClick={() => handleToggleChange(product.id, 'is_new_arrival', product.is_new_arrival)}
                  />
                  <ToggleBadge 
                    active={product.is_trending}
                    label="Trend"
                    icon={TrendingUp}
                    onClick={() => handleToggleChange(product.id, 'is_trending', product.is_trending)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white border border-charcoal/10 overflow-hidden">
          <table className="w-full">
            <thead className="bg-beige/50 border-b border-charcoal/10">
              <tr>
                <th className="w-12 p-4">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedProducts(products.map(p => p.id));
                      } else {
                        setSelectedProducts([]);
                      }
                    }}
                    className="w-4 h-4 accent-gold"
                  />
                </th>
                <th className="text-left p-4 text-xs tracking-wider uppercase text-charcoal-light font-medium">Product</th>
                <th className="text-left p-4 text-xs tracking-wider uppercase text-charcoal-light font-medium">Category</th>
                <th className="text-left p-4 text-xs tracking-wider uppercase text-charcoal-light font-medium">Price</th>
                <th className="text-left p-4 text-xs tracking-wider uppercase text-charcoal-light font-medium">Stock</th>
                <th className="text-left p-4 text-xs tracking-wider uppercase text-charcoal-light font-medium">Tags</th>
                <th className="text-right p-4 text-xs tracking-wider uppercase text-charcoal-light font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal/5">
              {products.map(product => (
                <tr key={product.id} className="hover:bg-beige/30 transition-colors">
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProducts([...selectedProducts, product.id]);
                        } else {
                          setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                        }
                      }}
                      className="w-4 h-4 accent-gold"
                    />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={product.images?.[0] || 'https://via.placeholder.com/60'}
                        alt={product.name}
                        className="w-12 h-12 object-cover"
                      />
                      <div>
                        <p className="font-medium text-charcoal">{product.name}</p>
                        <p className="text-xs text-charcoal-light">SKU: {product.sku || 'N/A'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-charcoal-light">{product.category_name || '-'}</td>
                  <td className="p-4">
                    {product.discount_price ? (
                      <div>
                        <span className="font-medium text-charcoal">₹{product.discount_price}</span>
                        <span className="text-xs text-charcoal-light line-through ml-2">₹{product.price}</span>
                      </div>
                    ) : (
                      <span className="font-medium text-charcoal">₹{product.price}</span>
                    )}
                  </td>
                  <td className="p-4">
                    <span className={`text-sm ${product.total_stock < 10 ? 'text-red-600 font-medium' : 'text-charcoal'}`}>
                      {product.total_stock || 0}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {product.is_featured && <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs">Featured</span>}
                      {product.is_best_seller && <span className="px-2 py-0.5 bg-orange-100 text-orange-600 text-xs">Best</span>}
                      {product.is_new_arrival && <span className="px-2 py-0.5 bg-green-100 text-green-600 text-xs">New</span>}
                      {product.is_trending && <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs">Trending</span>}
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEditModal(product)} className="p-2 hover:bg-beige">
                        <Edit className="w-4 h-4 text-charcoal-light" />
                      </button>
                      <button onClick={() => handleDuplicateProduct(product.id)} className="p-2 hover:bg-beige">
                        <Copy className="w-4 h-4 text-charcoal-light" />
                      </button>
                      <button onClick={() => setShowDeleteConfirm(product.id)} className="p-2 hover:bg-beige">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`w-10 h-10 ${
                currentPage === page 
                  ? 'bg-charcoal text-ivory' 
                  : 'bg-white border border-charcoal/20 text-charcoal hover:bg-beige'
              } text-sm font-medium`}
            >
              {page}
            </button>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4">
          <div className="bg-ivory max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <h3 className="font-heading text-xl text-charcoal">Confirm Delete</h3>
            </div>
            <p className="text-charcoal-light mb-6">
              Are you sure you want to delete this product? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-6 py-2.5 border border-charcoal/20 text-charcoal text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteProduct(showDeleteConfirm)}
                className="px-6 py-2.5 bg-red-600 text-white text-sm font-medium"
              >
                Delete Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-ivory max-w-4xl w-full my-8 shadow-xl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-ivory border-b border-charcoal/10 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-heading text-2xl font-light text-charcoal">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button onClick={() => setShowProductModal(false)} className="p-2 hover:bg-beige">
                <X className="w-5 h-5 text-charcoal" />
              </button>
            </div>
            
            {/* Tabs */}
            <div className="border-b border-charcoal/10 px-6">
              <div className="flex gap-6">
                {['basic', 'images', 'variants', 'toggles'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 text-sm font-medium transition-colors border-b-2 -mb-px ${
                      activeTab === tab 
                        ? 'border-gold text-charcoal' 
                        : 'border-transparent text-charcoal-light hover:text-charcoal'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'basic' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                        Product Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold"
                        placeholder="Enter product name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                        SKU
                      </label>
                      <input
                        type="text"
                        value={formData.sku}
                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                        className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold"
                        placeholder="e.g., JFH-001"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold resize-none"
                      placeholder="Product description..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                        Price (₹) *
                      </label>
                      <input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold"
                        placeholder="999"
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                        Discount Price (₹)
                      </label>
                      <input
                        type="number"
                        value={formData.discount_price}
                        onChange={(e) => setFormData({ ...formData, discount_price: e.target.value })}
                        className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold"
                        placeholder="799"
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                        Stock Quantity *
                      </label>
                      <input
                        type="number"
                        value={formData.stock}
                        onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                        className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold"
                        placeholder="100"
                        min="0"
                        data-testid="product-stock-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                        Fabric
                      </label>
                      <input
                        type="text"
                        value={formData.fabric}
                        onChange={(e) => setFormData({ ...formData, fabric: e.target.value })}
                        className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold"
                        placeholder="e.g., 100% Cotton"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                        Category *
                      </label>
                      <select
                        value={formData.category_id}
                        onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                        className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold"
                        disabled={categoriesLoading}
                      >
                        <option value="">
                          {categoriesLoading ? 'Loading categories...' : 'Select Category'}
                        </option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                      {categories.length === 0 && !categoriesLoading && (
                        <p className="text-xs text-red-500 mt-1">No categories found. Please add categories first.</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                        Available Sizes
                      </label>
                      <div className="flex flex-wrap gap-2 p-3 border border-charcoal/20 bg-white min-h-[50px]">
                        {getSizesForCategory(
                          categories.find(c => c.id === formData.category_id)?.name
                        ).map(size => (
                          <button
                            key={size}
                            type="button"
                            onClick={() => {
                              const newSizes = formData.sizes.includes(size)
                                ? formData.sizes.filter(s => s !== size)
                                : [...formData.sizes, size];
                              setFormData({ ...formData, sizes: newSizes });
                            }}
                            className={`px-3 py-1.5 text-xs font-medium transition-all ${
                              formData.sizes.includes(size)
                                ? 'bg-gold text-white'
                                : 'bg-charcoal/5 text-charcoal hover:bg-charcoal/10'
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-charcoal-light mt-1">Click to select/deselect sizes</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-2">
                        Tags (comma separated)
                      </label>
                      <input
                        type="text"
                        value={formData.tags.join(', ')}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                        })}
                        className="w-full px-4 py-3 border border-charcoal/20 bg-transparent focus:outline-none focus:border-gold"
                        placeholder="casual, summer, cotton"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'images' && (
                <div className="space-y-6">
                  <ImageUpload
                    onUploadSuccess={(url) => setFormData({ ...formData, images: [...formData.images, url] })}
                  />
                  
                  {formData.images.length > 0 && (
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-charcoal-light mb-4">
                        Uploaded Images
                      </label>
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                        {formData.images.map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img src={img} alt={`Product ${idx}`} className="w-full aspect-square object-cover border border-charcoal/10" />
                            <button
                              onClick={() => setFormData({
                                ...formData,
                                images: formData.images.filter((_, i) => i !== idx)
                              })}
                              className="absolute top-2 right-2 p-1 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'variants' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-charcoal">Product Variants</h3>
                      <p className="text-sm text-charcoal-light">Add size/color combinations with stock levels</p>
                    </div>
                    <button
                      onClick={addVariant}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-charcoal text-ivory text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Variant
                    </button>
                  </div>
                  
                  {formData.variants.length === 0 ? (
                    <div className="text-center py-12 border border-dashed border-charcoal/20">
                      <Package className="w-8 h-8 mx-auto mb-3 text-charcoal-light" />
                      <p className="text-charcoal-light">No variants added yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {formData.variants.map((variant, idx) => (
                        <div key={variant.id} className="p-4 bg-beige/50 border border-charcoal/10 flex flex-wrap gap-4 items-center">
                          <div className="flex-1 min-w-[100px]">
                            <label className="block text-xs text-charcoal-light mb-1">Size</label>
                            <select
                              value={variant.size}
                              onChange={(e) => updateVariant(idx, 'size', e.target.value)}
                              className="w-full px-3 py-2 border border-charcoal/20 bg-white text-sm"
                            >
                              {getSizesForCategory(
                                categories.find(c => c.id === formData.category_id)?.name
                              ).map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex-1 min-w-[100px]">
                            <label className="block text-xs text-charcoal-light mb-1">Color</label>
                            <input
                              type="text"
                              value={variant.color}
                              onChange={(e) => updateVariant(idx, 'color', e.target.value)}
                              className="w-full px-3 py-2 border border-charcoal/20 bg-white text-sm"
                              placeholder="Black"
                            />
                          </div>
                          <div className="w-24">
                            <label className="block text-xs text-charcoal-light mb-1">Stock</label>
                            <input
                              type="number"
                              value={variant.stock}
                              onChange={(e) => updateVariant(idx, 'stock', e.target.value)}
                              className="w-full px-3 py-2 border border-charcoal/20 bg-white text-sm"
                            />
                          </div>
                          <div className="flex-1 min-w-[100px]">
                            <label className="block text-xs text-charcoal-light mb-1">EAN (Item Code)</label>
                            <input
                              type="text"
                              value={variant.ean || ''}
                              onChange={(e) => updateVariant(idx, 'ean', e.target.value)}
                              className="w-full px-3 py-2 border border-charcoal/20 bg-white text-sm"
                              placeholder="8901234567001"
                            />
                          </div>
                          <div className="flex-1 min-w-[100px]">
                            <label className="block text-xs text-charcoal-light mb-1">SKU</label>
                            <input
                              type="text"
                              value={variant.sku || ''}
                              onChange={(e) => updateVariant(idx, 'sku', e.target.value)}
                              className="w-full px-3 py-2 border border-charcoal/20 bg-white text-sm"
                              placeholder="JFH-001-M-BLK"
                            />
                          </div>
                          <button
                            onClick={() => removeVariant(idx)}
                            className="p-2 text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'toggles' && (
                <div className="space-y-6">
                  <p className="text-charcoal-light">
                    Control where this product appears on your storefront.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'is_featured', label: 'Featured Product', icon: Star, desc: 'Show in Featured section' },
                      { key: 'is_best_seller', label: 'Best Seller', icon: Flame, desc: 'Mark as best selling' },
                      { key: 'is_new_arrival', label: 'New Arrival', icon: Sparkles, desc: 'Show in New Arrivals' },
                      { key: 'is_super_selling', label: 'Super Selling', icon: TrendingUp, desc: 'Highlight as super selling' },
                      { key: 'is_trending', label: 'Trending', icon: TrendingUp, desc: 'Show in Trending section' },
                      { key: 'is_offer', label: 'On Offer', icon: Tag, desc: 'Display offer badge' },
                      { key: 'is_premium_collection', label: 'Premium Collection', icon: Crown, desc: 'Include in premium collection' }
                    ].map(toggle => (
                      <label
                        key={toggle.key}
                        className={`flex items-center gap-4 p-4 border cursor-pointer transition-colors ${
                          formData[toggle.key] 
                            ? 'border-gold bg-gold/5' 
                            : 'border-charcoal/10 hover:border-charcoal/30'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData[toggle.key]}
                          onChange={(e) => setFormData({ ...formData, [toggle.key]: e.target.checked })}
                          className="w-5 h-5 accent-gold"
                        />
                        <toggle.icon className={`w-5 h-5 ${formData[toggle.key] ? 'text-gold' : 'text-charcoal-light'}`} />
                        <div>
                          <p className="font-medium text-charcoal">{toggle.label}</p>
                          <p className="text-xs text-charcoal-light">{toggle.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-ivory border-t border-charcoal/10 px-6 py-4 flex justify-end gap-4">
              <button
                onClick={() => setShowProductModal(false)}
                className="px-6 py-3 border border-charcoal/20 text-charcoal text-sm font-medium hover:bg-beige transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProduct}
                disabled={saving}
                className="px-8 py-3 bg-charcoal text-ivory text-sm font-medium hover:bg-charcoal/90 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingProduct ? 'Update Product' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
